import * as React from 'react';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

export const ItemStatus = strEnum(['SUCCESS', 'ERROR', 'AWAITING', 'PENDING']);
type ItemStatusType = keyof typeof ItemStatus;

type Device = { deviceId: string; serialId: string; name: string; };

/**
 * SendItemはnameプロパティを最低限もつ必要がある
 */
type SendItem = {
    name: string;
} & Record<string, any>; // その他のプロパティは自由

/**
 * デバイスに送信するものとデバイスの宛先の配列型。
 */
export type SendResult = {
    device: Device;
    sendItemList: { sendItem: SendItem; result: ItemStatusType }[];
};

const convertResponseToIcon = (response: ItemStatusType | ItemStatusType[]): React.JSX.Element => {
    const responseList = typeof response === 'string' ? [response] : response;

    if (responseList.some((item) => !Object.values(ItemStatus).includes(item))) {
        return <Typography>？</Typography>;
    } else if (responseList.every((item) => item === ItemStatus.PENDING)) {
        return <Typography>---</Typography>;
    } else if (responseList.every((item) => item === ItemStatus.SUCCESS)) {
        return <TaskAltIcon color='success' />;
    } else if (responseList.includes(ItemStatus.AWAITING)) {
        return <CircularProgress size={20} data-testid={'CircularProgress'} />;
    } else if (responseList.includes(ItemStatus.ERROR)) {
        return <ErrorOutlineIcon color='error' />;
    } else {
        return <Typography>？</Typography>;
    }
}
const Row = (props: { sendResult: SendResult }) => {
    const [isOpen, setIsOpen] = React.useState<boolean>(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset', padding: '0px' } }}>
                <TableCell align='center' width={'60px'} height={'20px'}>
                    <IconButton aria-label='expand row' size='small' onClick={() => setIsOpen(!isOpen)}>
                        {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell align='center' width={'300px'} height={'20px'}>
                    {props.sendResult.device.name}
                </TableCell>
                <TableCell align='center' width={'100px'} height={'20px'}>
                    {convertResponseToIcon(props.sendResult.sendItemList.map((item) => item.result))}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ padding: 0 }} colSpan={3}>
                    <Collapse data-testid='collapse' in={isOpen} timeout='auto' unmountOnExit>
                        <Box sx={{ margin: 0 }}>
                            <Table size='small'>
                                <TableBody>
                                    {props.sendResult.sendItemList.map((item, index) => (
                                        <TableRow key={index} sx={{ '& > *': { borderBottom: 'unset', padding: '0px' } }}>
                                            <TableCell width={'60px'} height={'20px'} />
                                            <TableCell align='center' width={'300px'} height={'20px'} sx={{ padding: '8px' }}>
                                                {item.sendItem?.name || 'No name'} {/* sendItem がない場合は 'No name' を表示 */}
                                            </TableCell>
                                            <TableCell align='center' width={'100px'} height={'20px'}>
                                                {convertResponseToIcon(item.result)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

type Props = {
    sendResultList: SendResult[];
};

const ResultListTable = (props: Props) => {
    return (
        <React.Fragment>
            <Typography variant='h6'>送信結果</Typography>
            <TableContainer>
                <Card variant='outlined'>
                    <Table size='small'>
                        <TableHead>
                            <TableRow sx={{ '& > *': { padding: '0px' } }}>
                                <TableCell width={'60px'} />
                                <TableCell align='center'>デバイス</TableCell>
                                <TableCell align='center' width={'100px'}>
                                    送信結果
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.sendResultList.map((sendResult, index) => (
                                <Row sendResult={sendResult} key={index} />
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            </TableContainer>
        </React.Fragment>
    );
};


export default ResultListTable;

function strEnum<T extends string>(o: Array<T>): { [K in T]: K } {
    return o.reduce((res, key) => {
        res[key] = key;
        return res;
    }, Object.create(null));
}